import { useEffect, useState } from 'react'

import { slackTwinsApi } from '@/lib/api'
import { useAuth } from '@/lib/config/auth-provider'

/**
 * Slack Twins is visible only when BOTH hold:
 *  - the org flag (`user.featureFlags.slackTwins`, off by default), and
 *  - the backend reports Slack as configured (`GET /slack-twins/install/status`
 *    `available`). With the Slack env unset the backend does not mount the
 *    route at all, so a failed request also reads as "not configured".
 *
 * While the check is pending, or when the flag is off (no request is made),
 * this is false, so no title or skeleton of a Slack surface ever flashes.
 */
export function useSlackTwinsEnabled(): boolean {
  const { user } = useAuth()
  const isFlagOn = user?.featureFlags?.slackTwins === true

  const [isConfigured, setIsConfigured] = useState(false)

  useEffect(() => {
    if (!isFlagOn) {
      setIsConfigured(false)
      return
    }
    let isCancelled = false
    Promise.resolve()
      .then(() => slackTwinsApi.getInstallStatus())
      .then((status) => {
        if (!isCancelled) {
          setIsConfigured(status.available === true)
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setIsConfigured(false)
        }
      })
    return () => {
      isCancelled = true
    }
  }, [isFlagOn])

  return isFlagOn && isConfigured
}
